import { Component, createRef, PureComponent, useCallback, useEffect } from 'react';
import styles from './styles.scss';
import Image, { getSrc, getEmbedUrl, INITIAL_DPR } from 'components/Shared/Image';
import { MediaSize } from 'models/ListViewProduct';
import { isHtmlElement, NO_ELEMENT } from 'utils';
import { BhVideo } from 'models/BhVideo';
import { IProductMedia } from 'models/IProductMedia';
import connect from 'components/Shared/connect';
import { BhImage } from 'models/BhImage';
import { isMobileLayout } from 'components/Shared/responsive';
import { emptyFunction } from 'utils/emptyFunction';
import { usePrevious } from 'hooks';
import { useImageAttributeContext } from 'views/ProductPage/configurators/imageAttributeContext';
import { isVideo, isImage } from 'views/ProductPage/selector';
import { bigMediaActive, bigMediaInactive } from 'views/ProductPage/actions';

const MEDIUM = 414;
const LARGE = 673;
const ZOOM = 1500;

const MEDIA_DIMENSIONS: { [key: number]: { width: number; height: number } } = {
  [MediaSize.Medium]: { width: MEDIUM, height: MEDIUM },
  [MediaSize.Large]: { width: LARGE, height: LARGE },
  [MediaSize.Zoom]: { width: ZOOM, height: ZOOM }
};

export const mediaSize = (size: MediaSize) =>
  size in MEDIA_DIMENSIONS ? MEDIA_DIMENSIONS[size] : MEDIA_DIMENSIONS[0];

interface BigMediaLifecycle {
  onMediaActive: () => void;
  onMediaInactive: () => void;
}
interface ProductImageProps extends BigMediaLifecycle {
  image: BhImage;
  width: number;
  height: number;
  isBot: boolean;
  cdnUrl: string;
  disabledZoom: boolean;
  displayName?: string;
  description?: string;
  onClick?: () => void;
}

interface ProductImageState {
  isZooming: boolean;
}

const HUNDRED = 100;

class ProductImage extends Component<ProductImageProps, ProductImageState> {
  public readonly state = {
    isZooming: false
  };
  private ref = createRef<HTMLDivElement>();

  public render() {
    const { width, height, isBot, image, onClick, disabledZoom, displayName, description } =
      this.props;
    return (
      <div
        role="button"
        onClick={onClick}
        ref={this.ref}
        className={styles.productImageContainer}
        onMouseEnter={disabledZoom ? emptyFunction : this.setZooming}
        onMouseMove={disabledZoom ? emptyFunction : this.zoom}
        onMouseLeave={disabledZoom ? emptyFunction : this.setNotZooming}
      >
        <Image
          className={styles.productImage}
          wrapperStyle={this.getWrapperStyle()}
          paddingBottom
          width={width}
          height={height}
          isBot={isBot}
          {...image}
        />
        {(description || displayName) && (
          <div className={styles.attributeHoverInfo}>
            {displayName && <p className={styles.attributeHoverDisplayName}>{displayName}</p>}
            {description && <p className={styles.attributeHoverDescription}>{description}</p>}
          </div>
        )}
      </div>
    );
  }

  private getWrapperStyle = () => ({
    opacity: this.state.isZooming ? 0 : 1
  });

  private setZooming = () =>
    this.setState(
      () => ({ isZooming: true }),
      () => {
        this.props.onMediaActive();
        if (this.ref.current === null) {
          return;
        }
        this.ref.current.style.setProperty('background-color', 'white');
        this.ref.current.style.setProperty('background-image', `url(${this.getZoomUrl()})`);
      }
    );
  private setNotZooming = () =>
    this.setState(
      () => ({ isZooming: false }),
      () => {
        this.props.onMediaInactive();
        if (this.ref.current === null) {
          return;
        }
        this.ref.current.style.setProperty('background-color', 'transparent');
        this.ref.current.style.setProperty('background-image', 'none');
      }
    );

  private getZoomUrl = () => {
    const { image, cdnUrl, isBot } = this.props;
    return getSrc(
      {
        ...image,
        ...mediaSize(MediaSize.Zoom),
        isBot,
        cdnUrl
      },
      INITIAL_DPR
    );
  };

  private zoom = (e: React.MouseEvent<HTMLElement>) => {
    const element = this.ref.current;
    const nativeEvent = e.nativeEvent;

    if (!element || !isHtmlElement(nativeEvent.target)) {
      return;
    }

    const { offsetX, offsetY } = nativeEvent;
    const { offsetWidth, offsetHeight } = nativeEvent.target;

    element.style.setProperty(
      'background-position',
      `${(HUNDRED * offsetX) / offsetWidth}% ${(HUNDRED * offsetY) / offsetHeight}%`
    );
    if (!this.state.isZooming) {
      this.setZooming();
    }
  };
}

interface ProductVideoProps extends BigMediaLifecycle {
  media: BhVideo;
  width: number;
  height: number;
}

class ProductVideo extends PureComponent<ProductVideoProps> {
  public componentDidMount() {
    this.props.onMediaActive();
  }

  public componentWillUnmount() {
    this.props.onMediaInactive();
  }

  public render() {
    const { width, height, media } = this.props;

    return (
      <div
        className={styles.videoFrameWrapper}
        style={{ paddingBottom: `${(height / width) * 100}%` }}
      >
        <iframe className={styles.videoFrame} frameBorder="none" src={getEmbedUrl(media.videoId)} />
      </div>
    );
  }
}

interface OwnProps {
  media: IProductMedia;
  size: MediaSize;
  productId: string;
  onClick?: () => void;
}

interface MergedProps extends OwnProps {
  isBot: boolean;
  cdnUrl: string;
  isMobile: boolean;
  onMediaActive: () => void;
  onMediaInactive: () => void;
}

const _BigProductMedia = ({
  media,
  size,
  isBot,
  cdnUrl,
  onClick,
  isMobile,
  productId,
  ...lifecycle
}: MergedProps) => {
  const mediaDimensions = mediaSize(size);
  const { hoverImage, displayName, description, productImage, resetProductImage } =
    useImageAttributeContext();

  const onMediaActive = useCallback(() => lifecycle.onMediaActive(), [lifecycle]);
  const onMediaInactive = useCallback(() => lifecycle.onMediaInactive(), [lifecycle]);
  const previousProductId = usePrevious(productId);

  const image = hoverImage || productImage || media;

  useEffect(() => {
    if (productImage !== undefined && productId !== previousProductId) {
      resetProductImage();
    }
  }, [productId, productImage, previousProductId, resetProductImage]);

  if (isVideo(media) && hoverImage === undefined && productImage === undefined) {
    return <ProductVideo media={media} {...mediaDimensions} {...lifecycle} />;
  }

  if (isImage(image)) {
    return (
      <ProductImage
        key={image.id}
        {...mediaDimensions}
        onMediaActive={onMediaActive}
        onMediaInactive={onMediaInactive}
        onClick={onClick}
        image={image}
        isBot={isBot}
        cdnUrl={cdnUrl}
        disabledZoom={isMobile}
        description={description}
        displayName={displayName}
      />
    );
  }

  return NO_ELEMENT;
};

export const BigProductMedia = connect<MergedProps, OwnProps>(
  ({
    currentBreakpoint,
    appShell: {
      isBot,
      links: { cdnUrl }
    }
  }) => ({
    isBot,
    cdnUrl,
    isMobile: isMobileLayout({ currentBreakpoint })
  }),
  dispatch => ({
    onMediaActive: () => dispatch(bigMediaActive()),
    onMediaInactive: () => dispatch(bigMediaInactive())
  })
)(_BigProductMedia);
